import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Plus, X } from 'lucide-react';
import { SearchableSelect } from './searchable-select';
import { joinFullName } from '../../utils/patientName';
import { usePatient, usePatients, useSearchPatients } from '../../services/httpServices/queries';
import { useActiveVisitsByPatient, useCreateVisit } from '../../services/httpServices/queries/useVisits';
import { useQueryClient } from '@tanstack/react-query';

interface PatientVisitSelectorProps {
  patientId: string;
  visitId: string;
  onChange: (values: { patientId: string; visitId: string }) => void;
  labelPrefix?: string;
  required?: boolean;
  disabled?: boolean;
  onAddNewPatient?: (query: string) => void;
  /** Tighter spacing and single-line status hints, for dense side-by-side cards. */
  compact?: boolean;
  /** Validation message rendered right-aligned in the Patient label row. */
  errorMessage?: string;
  /**
   * Render only the patient picker. Used by flows that create their own
   * encounter automatically (e.g. an IPD admission opens a dedicated IPD
   * visit), so asking staff for a visit would be both redundant and a way to
   * attach the wrong care setting's bill.
   */
  hideVisit?: boolean;
  /**
   * The screen's backend opens the OPD visit when it is missing. Used by the
   * diagnostic counter, which must not force staff to create one manually.
   */
  autoCreateVisit?: boolean;
  /**
   * Restrict the selectable encounters to one care setting (e.g. the
   * diagnostic counter only sells OPD tests, so it must never attach a receipt
   * to the patient's separate IPD visit).
   */
  visitType?: 'OPD' | 'IPD' | 'ER' | 'EXT';
}

export function PatientVisitSelector({
  patientId,
  visitId,
  onChange,
  labelPrefix = '',
  required = true,
  disabled = false,
  onAddNewPatient,
  compact = false,
  errorMessage,
  hideVisit = false,
  autoCreateVisit = false,
  visitType,
}: PatientVisitSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: patientsData, isLoading: patientsLoading } = usePatients(1, 100, {
    isActive: true,
  });
  const { data: searchData, isLoading: searchLoading } = useSearchPatients(searchQuery);
  // Editing a record whose patient was later deactivated: fetch them so the
  // picker still shows a label instead of going blank.
  const { data: selectedPatientData } = usePatient(patientId);
  const { data: activeVisitsData, isLoading: visitsLoading } = useActiveVisitsByPatient(patientId);
  const createVisit = useCreateVisit();
  const queryClient = useQueryClient();

  const [showQuickVisit, setShowQuickVisit] = useState(false);
  const [quickVisitType, setQuickVisitType] = useState<'OPD' | 'IPD' | 'ER' | 'EXT'>('OPD');
  const [quickVisitComplaint, setQuickVisitComplaint] = useState('');
  const [quickVisitError, setQuickVisitError] = useState<string | null>(null);

  const patients = useMemo(() => {
    if (searchQuery.length >= 2) {
      return searchData?.data || [];
    }
    return patientsData?.data || [];
  }, [searchQuery, searchData, patientsData]);

  const isPatientsLoading = searchQuery.length >= 2 ? searchLoading : patientsLoading;
  const activeVisits = useMemo(() => {
    const visits = activeVisitsData?.data || [];
    if (!visitType) return visits;
    return visits.filter(
      (visit: any) => (visit?.type ?? visit?.visitType) === visitType,
    );
  }, [activeVisitsData, visitType]);

  const patientOptions = useMemo(() => {
    const options = patients.map((p: any) => ({
      value: p.id,
      label: joinFullName(p) + (p.mobileNumber ? ` (${p.mobileNumber})` : ''),
    }));

    const selected = selectedPatientData?.data;
    if (patientId && selected && !options.some((option) => option.value === patientId)) {
      const name = joinFullName(selected) || selected.registrationNumber || 'Patient';
      const meta = selected.mobileNumber || selected.registrationNumber;
      const status = selected.isActive === false ? ' — inactive' : '';
      options.unshift({
        value: selected.id,
        label: `${meta && meta !== name ? `${name} (${meta})` : name}${status}`,
      });
    }

    return options;
  }, [patients, patientId, selectedPatientData]);

  // Auto-resolve visit when patient changes
  useEffect(() => {
    // The caller owns encounter creation; never force a visit on it.
    if (hideVisit) return;
    if (!patientId) {
      if (visitId) {
        onChange({ patientId: '', visitId: '' });
      }
      return;
    }

    if (activeVisits.length === 0) {
      // No active visits — clear visitId if currently set
      if (visitId) {
        onChange({ patientId, visitId: '' });
      }
      return;
    }

    if (activeVisits.length === 1) {
      // Exactly one active visit — auto-select it
      const onlyVisit = activeVisits[0];
      if (visitId !== onlyVisit.id) {
        onChange({ patientId, visitId: onlyVisit.id });
      }
      return;
    }

    // Multiple active visits — if current visitId is not in the list, clear it
    const validVisit = activeVisits.find((v: any) => v.id === visitId);
    if (!validVisit && visitId) {
      onChange({ patientId, visitId: '' });
    }
  }, [patientId, activeVisits, visitId, onChange, hideVisit]);

  /** Visits expose their kind as `type`; older payloads used `visitType`. */
  const visitTypeOf = (visit: any): string => visit?.type ?? visit?.visitType ?? '';

  const visitOptions = useMemo(() => {
    return activeVisits.map((v: any) => ({
      value: v.id,
      label: `${v.visitNumber} (${visitTypeOf(v)}) — ${new Date(v.visitDate).toLocaleDateString()}`,
    }));
  }, [activeVisits]);

  const selectedVisit = useMemo(() => {
    return activeVisits.find((v: any) => v.id === visitId);
  }, [activeVisits, visitId]);

  // Exactly one active visit — it is auto-selected, so the hint can sit inline
  // with the Patient label in compact layouts.
  const autoSelectedVisit = !visitsLoading && activeVisits.length === 1 ? selectedVisit : undefined;

  const patientLabel = (
    <span className="flex items-baseline justify-between gap-2">
      <span className="shrink-0">{`${labelPrefix}Patient ${required ? '*' : ''}`}</span>
      {errorMessage ? (
        <span className="shrink-0 text-right text-xs font-medium text-danger">{errorMessage}</span>
      ) : (
        compact &&
        autoSelectedVisit && (
          <span
            className="flex min-w-0 items-center gap-1 text-[11px] font-normal text-success"
            title={`${autoSelectedVisit.visitNumber} · ${visitTypeOf(autoSelectedVisit)} auto-selected`}
          >
            <CheckCircle2 size={12} className="shrink-0" />
            <span className="truncate">
              {autoSelectedVisit.visitNumber} · {visitTypeOf(autoSelectedVisit)} auto-selected
            </span>
          </span>
        )
      )}
    </span>
  );

  const handleQuickVisitCreate = async () => {
    setQuickVisitError(null);
    if (!patientId) return;
    try {
      await createVisit.mutateAsync(
        { patientId, type: quickVisitType, complaint: quickVisitComplaint || undefined },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['visits', 'active', patientId] });
            setShowQuickVisit(false);
            setQuickVisitComplaint('');
            setQuickVisitType('OPD');
          },
        }
      );
    } catch (err: any) {
      setQuickVisitError(err?.message || 'Failed to create visit');
    }
  };

  const patientField = (
    <SearchableSelect
      label={patientLabel}
      labelClassName="block text-sm font-medium text-text mb-1"
      value={patientId}
      onChange={(value) => onChange({ patientId: value, visitId: '' })}
      options={patientOptions}
      placeholder="Search by name or mobile..."
      loading={isPatientsLoading}
      disabled={disabled}
      onQueryChange={setSearchQuery}
      onAddNew={onAddNewPatient}
      addNewLabel="Add new patient"
      // The message sits in the label row; passing an sr-only node only keeps the danger border.
      error={errorMessage ? <span className="sr-only" /> : undefined}
    />
  );

  // A caller that creates its own encounter (e.g. an IPD admission) only needs
  // the patient. Showing a visit picker there would let the stay's bill be
  // attached to the wrong care setting.
  if (hideVisit) {
    return (
      <div className={compact ? 'space-y-2' : 'space-y-3'}>{patientField}</div>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {patientField}

      {patientId && visitsLoading && (
        <div className={`flex items-center gap-2 text-text-muted ${compact ? 'text-[11px]' : 'text-sm'}`}>
          <Loader2 size={compact ? 12 : 14} className="animate-spin" />
          Checking active visits...
        </div>
      )}

      {patientId && !visitsLoading && activeVisits.length === 0 && (
        autoCreateVisit ? (
          <div className="flex items-start gap-2 rounded-lg border border-info/20 bg-info/10 p-2">
            <CheckCircle2 size={compact ? 13 : 16} className="mt-0.5 shrink-0 text-info" />
            <p className={compact ? 'text-[11px] leading-snug text-text' : 'text-sm text-text'}>
              No active OPD visit yet — one will be created automatically when you
              save.
            </p>
          </div>
        ) : (
        <div
          className={`flex flex-col bg-warning/10 border border-warning/20 rounded-lg ${
            compact ? 'gap-2 p-2' : 'gap-3 p-3'
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertCircle size={compact ? 13 : 16} className="text-warning mt-0.5 shrink-0" />
            <div className={compact ? 'text-[11px] leading-snug text-text' : 'text-sm text-text'}>
              <p className="font-medium">No active visit found</p>
              <p className="text-text-muted">
                {compact
                  ? 'Create one to proceed.'
                  : 'This patient does not have an active visit. Please create one to proceed.'}
              </p>
            </div>
          </div>
          {!showQuickVisit ? (
            <button
              type="button"
              onClick={() => setShowQuickVisit(true)}
              className={`self-start bg-primary text-white font-medium rounded-md hover:bg-primary-dark transition-colors flex items-center gap-1.5 ${
                compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
              }`}
            >
              <Plus size={compact ? 12 : 14} />
              Create Visit
            </button>
          ) : (
            <div className={`bg-background rounded-lg border border-border ${compact ? 'p-2 space-y-2' : 'p-3 space-y-3'}`}>
              <div className="flex items-center justify-between">
                <p className={`font-medium text-text ${compact ? 'text-xs' : 'text-sm'}`}>Quick Create Visit</p>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickVisit(false);
                    setQuickVisitError(null);
                  }}
                  className="text-text-muted hover:text-text"
                >
                  <X size={14} />
                </button>
              </div>
              <div>
                <label className={`block font-medium text-text-muted mb-1 ${compact ? 'text-[11px]' : 'text-xs'}`}>
                  Visit Type
                </label>
                <select
                  value={quickVisitType}
                  onChange={(e) => setQuickVisitType(e.target.value as 'OPD' | 'IPD' | 'ER' | 'EXT')}
                  className={`w-full border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-1 focus:ring-primary ${
                    compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'
                  }`}
                >
                  <option value="OPD">OPD</option>
                  <option value="IPD">IPD</option>
                  <option value="ER">Emergency</option>
                  <option value="EXT">External</option>
                </select>
              </div>
              <div>
                <label className={`block font-medium text-text-muted mb-1 ${compact ? 'text-[11px]' : 'text-xs'}`}>
                  Complaint (optional)
                </label>
                <input
                  type="text"
                  value={quickVisitComplaint}
                  onChange={(e) => setQuickVisitComplaint(e.target.value)}
                  placeholder="e.g. Fever, Checkup..."
                  className={`w-full border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-1 focus:ring-primary ${
                    compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'
                  }`}
                />
              </div>
              {quickVisitError && (
                <p className={`text-danger ${compact ? 'text-[11px]' : 'text-xs'}`}>{quickVisitError}</p>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleQuickVisitCreate}
                  disabled={createVisit.isPending}
                  className={`bg-primary text-white font-medium rounded-md hover:bg-primary-dark transition-colors flex items-center gap-1.5 disabled:opacity-50 ${
                    compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
                  }`}
                >
                  {createVisit.isPending ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Plus size={12} />
                  )}
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickVisit(false);
                    setQuickVisitError(null);
                  }}
                  className={`text-text-muted hover:text-text transition-colors ${
                    compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        )
      )}

      {!compact && patientId && autoSelectedVisit && (
        <div className="flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
          <CheckCircle2 size={16} className="text-success mt-0.5 shrink-0" />
          <div className="text-sm text-text">
            <p className="font-medium">Active visit auto-detected</p>
            <p className="text-text-muted">
              {autoSelectedVisit.visitNumber} ({visitTypeOf(autoSelectedVisit)}) — started{' '}
              {new Date(autoSelectedVisit.visitDate).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}

      {patientId && !visitsLoading && activeVisits.length > 1 && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 p-2 bg-info/10 border border-info/20 rounded-lg">
            <AlertCircle size={compact ? 13 : 16} className="text-info mt-0.5 shrink-0" />
            <p className={compact ? 'text-[11px] leading-snug text-text' : 'text-sm text-text'}>
              Multiple active visits found. Please select the correct one.
            </p>
          </div>
          <SearchableSelect
            label="Select Visit *"
            labelClassName="block text-sm font-medium text-text mb-1"
            value={visitId}
            onChange={(value) => onChange({ patientId, visitId: value })}
            options={visitOptions}
            placeholder="Select active visit..."
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
